import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Pencil, Plus, Trash2 } from 'lucide-react'

import { api } from '@/api/client'
import { confirm } from '@/stores/confirm'
import type { PartnerAlias, PurchasePartnerHit } from '@/types/models'
import { useToast } from '@/stores/toast'
import { Modal } from '@/components/Modal'
import { PurchasePartnerCombo } from '@/components/PurchasePartnerCombo'
import { type BulkResult, PurchasePartnerMultiCombo, postToPartners } from '@/components/PurchasePartnerMultiCombo'
import { Field, inputCls } from '@/components/Field'
import { cn } from '@/lib/utils'
import { useDebouncedValue, useRenderLimit } from '@/lib/useListPerf'
import { ShowMoreRow } from '@/components/ShowMoreRow'

export interface AliasConfig {
  apiBase: string       // '/api/purchase/partner-aliases' | '/api/purchase/label-partner-aliases'
  queryKey: string
  aliasLabel: string    // '구매장용 거래처' | '라벨용 거래처'
}

interface AliasForm {
  id?: number
  partner: number | null
  partner_label: string
  alias: string
  partners: PurchasePartnerHit[]   // 추가: 여러 거래처에 같은 별칭(수정은 partner 한 곳)
}

const blank: AliasForm = { partner: null, partner_label: '', alias: '', partners: [] }

/** 거래처명 → 별칭(구매장용/라벨용 거래처) CRUD. 보기 화면에 이 별칭으로 표시. */
export function PartnerAliasManage({ config: c }: { config: AliasConfig }) {
  const qc = useQueryClient()
  const push = useToast((s) => s.push)
  const [search, setSearch] = useState('')
  const [edit, setEdit] = useState<AliasForm | null>(null)

  // 검색어는 입력이 멈춘 뒤 보내고, 새 결과가 올 때까지 이전 목록 유지.
  const q = useDebouncedValue(search.trim())
  const { data: rows = [] } = useQuery({
    queryKey: [c.queryKey, q],
    placeholderData: keepPreviousData,
    queryFn: () => api<PartnerAlias[]>(
      `${c.apiBase}${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  })

  const remove = useMutation({
    mutationFn: (id: number) => api(`${c.apiBase}/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [c.queryKey] }); push('삭제됨', 'success') },
    onError: (e) => push(`삭제 실패: ${e.message}`, 'error', 5000),
  })

  const save = useMutation({
    mutationFn: (f: AliasForm) => {
      if (f.id) return api(`${c.apiBase}/${f.id}`, { method: 'PATCH', body: { partner: f.partner, alias: f.alias.trim() } })
      return postToPartners(c.apiBase, f.partners.map((p) => p.id), { alias: f.alias.trim() })
    },
    onSuccess: (res) => {
      if (res === null) return              // 덮어쓰기 확인에서 취소 — 창 유지
      qc.invalidateQueries({ queryKey: [c.queryKey] })
      setEdit(null)
      const b = res as BulkResult
      push('created' in b ? `저장됨 — 새로 ${b.created}곳${b.updated ? `, 덮어쓰기 ${b.updated}곳` : ''}` : '저장됨', 'success')
    },
    onError: (e) => push(`저장 실패: ${e.message}`, 'error', 6000),
  })

  const list = useRenderLimit(rows, q)
  const canSubmit = !!edit && (edit.id ? !!edit.partner : edit.partners.length > 0) && !!edit.alias.trim()

  return (
    <div className="max-w-6xl mx-auto px-6 pt-4">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-sm font-bold text-slate-700">거래처 별칭 <span className="font-normal text-slate-400">(거래처 → {c.aliasLabel})</span></h3>
        <input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="거래처/별칭 검색"
               className={inputCls + ' ml-auto w-56 !py-1 text-xs'}/>
        <button onClick={() => setEdit({ ...blank })}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5"/> 별칭 추가
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide">
            <tr>
              <th className="px-3 py-2 text-left font-medium w-72">거래처</th>
              <th className="px-3 py-2 text-center font-medium w-6"></th>
              <th className="px-3 py-2 text-left font-medium">{c.aliasLabel}</th>
              <th className="px-3 py-2 text-right font-medium w-20"></th>
            </tr>
          </thead>
          <tbody>
            {list.shown.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-1.5 text-slate-800">{r.partner_name}</td>
                <td className="px-3 py-1.5 text-center text-slate-300"><ArrowRight className="w-3.5 h-3.5 inline"/></td>
                <td className="px-3 py-1.5 font-medium text-slate-900">{r.alias}</td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <button onClick={() => setEdit({ id: r.id, partner: r.partner, partner_label: r.partner_name, alias: r.alias, partners: [] })}
                          className="text-blue-600 hover:text-blue-800 mr-2"><Pencil className="w-3.5 h-3.5"/></button>
                  <button onClick={() => { void confirm(`'${r.partner_name}' 별칭 삭제?`, { danger: true }).then((ok) => ok && remove.mutate(r.id)) }}
                          className="text-rose-500 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5"/></button>
                </td>
              </tr>
            ))}
            <ShowMoreRow hidden={list.hidden} colSpan={4} onMore={list.more} onAll={list.all}/>
            {!rows.length && (
              <tr><td colSpan={4} className="text-center py-6 text-xs text-slate-400">
                거래처 별칭이 없습니다. "별칭 추가"로 시작하세요.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)}
             title={edit?.id ? '거래처 별칭 수정' : '거래처 별칭 추가'} size="lg">
        {edit && (
          <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) save.mutate(edit) }} className="space-y-3">
            <Field label={`거래처 * (전 회사)${edit.id ? '' : ' — 여러 곳 선택 가능'}`} full>
              {edit.id ? (
                <PurchasePartnerCombo label={edit.partner_label}
                  onChange={(p) => setEdit({ ...edit, partner: p.id, partner_label: p.name })}/>
              ) : (
                <PurchasePartnerMultiCombo value={edit.partners}
                  onChange={(v) => setEdit({ ...edit, partners: v })}/>
              )}
            </Field>
            <Field label={`${c.aliasLabel} *`} full>
              <input value={edit.alias} onChange={(e) => setEdit({ ...edit, alias: e.target.value })}
                     placeholder="보기 화면에 표시할 거래처명" className={inputCls}/>
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEdit(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
              <button type="submit" disabled={!canSubmit || save.isPending}
                      className={cn('px-4 py-2 text-sm font-semibold rounded-lg',
                        (!canSubmit || save.isPending) ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700 text-white')}>
                {save.isPending ? '저장 중...' : '저장'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
